import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';
import { GridColDef, } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium, GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-premium';
import { FC, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import LicenseContext from 'src/contexts/LicenseContext';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { TruckEntity } from 'src/entities/truckEntity';
import { useTrucksRequest as usePaginationRequest } from 'src/hooks/useTrucks.request';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { Columns } from '../V2TrucksPresenter/Columns';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (truckEntities: TruckEntity[]) => void;
  selectedCompanyId: number;
  currentTruckIds: number[];
};

const SelectTruckDialog: FC<Props> = memo((
  {
    open,
    onClose,
    onSelect,
    currentTruckIds,
    selectedCompanyId,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(
    0,
    sortCondition,
    searchCondition,
    Number.MAX_SAFE_INTEGER,
  );

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(licenseContext?.config?.unit).filter((col) => {
    if (licenseContext?.config?.unit_used_for_calculation === '重量と体積') {
      return true;
    }

    return col.field !== 'loading_platform_volume_m3';
  }), [licenseContext?.config]);

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids);
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids.map((it) => Number(it)));
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData?.data.map((it) => it.id) || [];
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const maybeSelectedTrucks = requestData?.data.filter((e) => selectedIds.includes(e.id));

  const onColumnWidthChange = (params: GridColumnResizeParams) => {
    const columns = dataGridColumns.map((col) => {
      let newDefinition = { field: col.field, width: col.width };
      if (col.field === params.colDef.field) {
        newDefinition = { ...newDefinition, width: params.colDef.width };
      }
      return newDefinition;
    });
    localStorage.setItem('truck-columns-width', JSON.stringify(columns));
  };

  const onSubmit = () => {
    const selectedEntities = requestData?.data.filter((e) => selectedIds.includes(e.id)) || [];
    onSelect(selectedEntities);
    onClose();
  };

  useEffect(() => {
    const maybeCompanyPredicateEntity: RansackPredicateEntity | null = selectedCompanyId ? {
      column: 'company_id',
      predicate: 'eq',
      value: `${selectedCompanyId}`,
      link: 'and'
    } : null;

    const modifiedAndConditions: RansackPredicateEntity[] = maybeCompanyPredicateEntity
      ? [
        maybeCompanyPredicateEntity,
        ...andConditions
      ] : andConditions;

    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', modifiedAndConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions, selectedCompanyId]);

  // 事前選択は初回マウント時の一度だけ。currentTruckIds は親が毎レンダー .map() で作り直すため、
  // 値の変化を seed のトリガーにすると親が再レンダーしただけで選択内容が巻き戻る。
  // ダイアログは開閉ごとに再マウントされ seededRef も毎回リセットされるため、開くたびに再 seed される。
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;

    seededRef.current = true;
    setSelectedIds(currentTruckIds || []);
  }, [currentTruckIds]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        mt: 8
      }}
    >
      {/* DialogTitle 自身が variant="h6" の <h2>。内側に Typography h6 を置くと <h2><h6> の不正ネストになる */}
      <DialogTitle>
        トラックを選択してください
      </DialogTitle>
      <DataGridPremium
        rows={requestData?.data || []}
        columns={dataGridColumns}
        pagination
        checkboxSelection
        disableRowGrouping
        initialState={{
          columns: {
            columnVisibilityModel: {
              garage_address: false,
            },
          },
        }}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        loading={requestIsLoading}
        onColumnWidthChange={(params) => onColumnWidthChange(params)}
        onRowSelectionModelChange={onSelectionModelChange}
        sortingMode="server"
        paginationMode="client"
        filterMode="server"
        // rowCount は paginationMode="client" では prop→内部 state の同期が行われず(server 限定)、
        // client 側の件数初期化はマウント時のみ。渡すと未キャッシュの初回オープンで件数が 0 に固定される。
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={onFilterChange}
        rowSelectionModel={{ ids: new Set(selectedIds), type: 'include' }}
        pageSizeOptions={[30]}
        paginationModel={{ page, pageSize }}
        // paginationMode="client"(全件取得済み)なのでページ送りは表示切替のみ。
        // 選択済み ID をクリアすると他ページで選んだ分と既存の設定値まで失われる。
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        sx={{
          mb: 2,
          mx: 2,
        }}
        keepNonExistentRowsSelected
        disableAggregation
        disablePivoting
      />
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
        >
          キャンセル
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
        >
          {
            (() => {
              if (!maybeSelectedTrucks) return '設定する';
              if (maybeSelectedTrucks.length === 0) return '設定する';
              if (maybeSelectedTrucks.length === 1) return `${maybeSelectedTrucks[0].license_plate_value} を設定する`;
              const lastSelectedTruck = [...maybeSelectedTrucks].slice(-1)[0];
              const selectedOtherTrucksLength = maybeSelectedTrucks.length - 1;

              return `${lastSelectedTruck.license_plate_value} 他${selectedOtherTrucksLength}台を設定する`;
            })()
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SelectTruckDialog;
